import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Send, Building, Sun, Palette } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const interessen = [
  { id: "immobilien", label: "Immobilien", icon: Building },
  { id: "photovoltaik", label: "Photovoltaik", icon: Sun },
  { id: "kunst", label: "Kunst & Alternatives", icon: Palette },
];

const optionen = {
  objekttyp: ["Neubau", "Denkmal / Sanierung", "Bestandsobjekt"],
  horizont: ["Kurzfristig (< 5 J.)", "Mittelfristig (5–15 J.)", "Langfristig (> 15 J.)"],
  ziel: ["Steuerersparnis", "Vermögensaufbau", "Krisenabsicherung"],
};

const stepVariants = {
  enter: { opacity: 0, x: 32 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -32 },
};

const OptionPill = ({ label, selected, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`border px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
      selected
        ? "border-petrol bg-petrol text-paper"
        : "border-petrol/20 bg-white text-slate-600 hover:border-petrol"
    }`}
  >
    {label}
  </button>
);

export const CTASection = () => {
  const [step, setStep] = useState(0);
  const [interesse, setInteresse] = useState("");
  const [objekttyp, setObjekttyp] = useState("");
  const [horizont, setHorizont] = useState("");
  const [ziel, setZiel] = useState("");
  const [form, setForm] = useState({ name: "", email: "", telefon: "", nachricht: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const toastLabels = { immobilien: "Immobilien", photovoltaik: "Photovoltaik", kunst: "Kunst" };

  const next = () => {
    if (step === 0 && !interesse) {
      setError("Bitte wählen Sie einen Themenbereich.");
      return;
    }
    setError("");
    setStep((s) => Math.min(s + 1, 2));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      setError("Bitte geben Sie Ihren Namen und eine gültige E-Mail-Adresse an.");
      return;
    }
    setError("");
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/check-anfrage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interesse, objekttyp, horizont, ziel, ...form }),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success(`${toastLabels[interesse]}-Check angefordert`, {
        description: `Vielen Dank, ${form.name}! Ihre Anfrage wurde an info@fiplago.de gesendet — Sie erhalten eine Eingangsbestätigung per E-Mail.`,
      });
      setStep(0);
      setInteresse("");
      setObjekttyp("");
      setHorizont("");
      setZiel("");
      setForm({ name: "", email: "", telefon: "", nachricht: "" });
    } catch {
      toast.error("Senden fehlgeschlagen", {
        description: "Bitte versuchen Sie es erneut oder schreiben Sie direkt an info@fiplago.de.",
      });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-petrol/20 bg-white px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-petrol";

  return (
    <section id="check" data-testid="cta-demo-form" className="bg-paper py-24 lg:py-36 grain relative">
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 px-4 sm:px-8 lg:px-16 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow mb-4">Der nächste Schritt</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-petrol">
              Ihr Immobilien-Check — kostenfrei & unverbindlich
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Beantworten Sie drei kurze Fragen — wir zeigen Ihnen, welches
              Abschreibungspotenzial in Ihrer Situation steckt.
            </p>
            <div className="mt-10 space-y-4">
              {["Persönliche Ersteinschätzung", "Passende Gestaltungsmodelle", "Konkrete nächste Schritte"].map((t, i) => (
                <div key={t} className="flex items-center gap-4">
                  <span className="font-mono text-xs text-gold">0{i + 1}</span>
                  <span className="text-sm font-medium text-slate-700">{t}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-sm text-slate-600">
              Oder schreiben Sie uns direkt:{" "}
              <a
                data-testid="cta-email-link"
                href="mailto:info@fiplago.de"
                className="font-semibold text-petrol underline decoration-gold/60 underline-offset-4 transition-colors hover:text-forest"
              >
                info@fiplago.de
              </a>
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} className="border border-petrol/15 bg-white p-8 sm:p-10 shadow-xl shadow-petrol/5" data-testid="cta-form">
            <div className="mb-8 flex items-center gap-3" data-testid="cta-form-progress">
              {[0, 1, 2].map((i) => (
                <span key={i} className={`h-1 flex-1 transition-colors duration-500 ${i <= step ? "bg-petrol" : "bg-petrol/15"}`} />
              ))}
              <span className="font-mono text-xs text-slate-500">{step + 1} / 3</span>
            </div>

            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div key="s0" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
                  <p className="font-display text-xl font-medium text-petrol">Wofür interessieren Sie sich?</p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {interessen.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        data-testid={`cta-interest-${opt.id}`}
                        onClick={() => { setInteresse(opt.id); setError(""); }}
                        className={`flex flex-col items-start gap-3 border p-5 text-left transition-all duration-300 ${
                          interesse === opt.id ? "border-petrol bg-petrol text-paper" : "border-petrol/20 hover:border-petrol text-slate-700"
                        }`}
                      >
                        <opt.icon size={22} className={interesse === opt.id ? "text-gold" : "text-forest"} />
                        <span className="text-sm font-semibold">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
                  <p className="font-display text-xl font-medium text-petrol">Ihre Rahmenbedingungen</p>
                  <div className="mt-6 space-y-7">
                    <div>
                      <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Objekttyp</p>
                      <div className="flex flex-wrap gap-3">
                        {optionen.objekttyp.map((o) => (
                          <OptionPill key={o} label={o} selected={objekttyp === o} onClick={() => setObjekttyp(o)} testid={`cta-objekttyp-${optionen.objekttyp.indexOf(o)}`} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Investitionshorizont</p>
                      <div className="flex flex-wrap gap-3">
                        {optionen.horizont.map((o) => (
                          <OptionPill key={o} label={o} selected={horizont === o} onClick={() => setHorizont(o)} testid={`cta-horizont-${optionen.horizont.indexOf(o)}`} />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Primäres Ziel</p>
                      <div className="flex flex-wrap gap-3">
                        {optionen.ziel.map((o) => (
                          <OptionPill key={o} label={o} selected={ziel === o} onClick={() => setZiel(o)} testid={`cta-ziel-${optionen.ziel.indexOf(o)}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
                  <p className="font-display text-xl font-medium text-petrol">Ihre Kontaktdaten</p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      data-testid="cta-input-name"
                      className={inputCls}
                      placeholder="Name *"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                    <input
                      data-testid="cta-input-email"
                      type="email"
                      className={inputCls}
                      placeholder="E-Mail *"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                    <input
                      data-testid="cta-input-telefon"
                      className={`${inputCls} sm:col-span-2`}
                      placeholder="Telefon (optional)"
                      value={form.telefon}
                      onChange={(e) => setForm({ ...form, telefon: e.target.value })}
                    />
                    <textarea
                      data-testid="cta-input-nachricht"
                      className={`${inputCls} sm:col-span-2 min-h-[96px] resize-none`}
                      placeholder="Ihre Nachricht (optional)"
                      value={form.nachricht}
                      onChange={(e) => setForm({ ...form, nachricht: e.target.value })}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <p data-testid="cta-form-error" className="mt-5 text-sm font-medium text-red-600">{error}</p>
            )}

            <div className="mt-8 flex items-center justify-between gap-4">
              <button
                type="button"
                data-testid="cta-btn-back"
                onClick={() => { setError(""); setStep((s) => Math.max(s - 1, 0)); }}
                className={`inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-opacity hover:text-petrol ${step === 0 ? "pointer-events-none opacity-0" : "opacity-100"}`}
              >
                <ArrowLeft size={15} /> Zurück
              </button>
              {step < 2 ? (
                <button type="button" data-testid="cta-btn-next" onClick={next} className="btn-primary">
                  Weiter <ArrowRight size={15} />
                </button>
              ) : (
                <button type="submit" data-testid="cta-btn-submit" disabled={sending} className="btn-primary disabled:opacity-60">
                  {sending ? "Wird gesendet …" : "Anfrage senden"} <Send size={15} />
                </button>
              )}
            </div>

            <p className="mt-6 border-t border-petrol/10 pt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400" data-testid="cta-hinweis">
              Ihre Anfrage wird direkt an info@fiplago.de gesendet.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
